/* locality.js — ONE "Suburb or postcode" field for the whole theme.
 * Loaded by sections/header.liquid (every page) with `defer`; the header
 * "Deliver to" chip (header-tabbed.js) and the PDP Delivery card
 * (purchase-block.js) both attach it to their own input.
 *
 * Luke, 2026-09-09: "We should have some way of determining the suburb
 * based on the postcode and utilise the name here. User should be able to
 * type suburb name, or postcode."
 *
 * Owns:
 *  1. STORAGE. localStorage st.locality = {"suburb","state","postcode"}
 *     — the resolved place that goes with st.postcode (which stays the
 *     4-digit value every existing consumer reads). A bare postcode with no
 *     suburb has no st.locality; a locality whose postcode disagrees with
 *     st.postcode is treated as absent.
 *  2. LOOKUP. GET <shipping_proxy_base>/locality?q=<text> (the shipping
 *     app's App Proxy) -> { results: [{ suburb, state, postcode }] } (<= 8).
 *     Results are cached per query for the page; a non-2xx (the proxy is
 *     404 until the app is installed on the store) or a network failure
 *     parks the endpoint for 60 s and the field degrades to today's
 *     behaviour: a 4-digit postcode is accepted as typed.
 *  3. TYPEAHEAD. attach(input, {list, onSelect, onPostcode}) — combobox +
 *     listbox (WAI-ARIA APG pattern): debounced 150 ms, min 2 chars, arrow
 *     keys / Enter / Escape, aria-activedescendant. Rows read
 *     "Lane Cove NSW 2064". Typing a full 4-digit postcode with exactly one
 *     match auto-resolves it; with several, the list opens so the shopper
 *     picks the suburb.
 *
 * API: window.SydneyTaps.locality = { get, set, label, search, attach,
 * ENDPOINT }. Nothing here writes st.postcode — the chip does that. */
(function () {
  'use strict';
  var ST = (window.SydneyTaps = window.SydneyTaps || {});
  if (ST.locality) return;

  var KEY = 'st.locality';
  // Base comes from the theme setting shipping_proxy_base via locality-boot's
  // inline script (window.SydneyTaps.__proxy), so moving shipping to its own
  // app is the one setting change it was meant to be. Found on 2026-09-16:
  // the PDP and cart quotes had followed the setting to the new app while
  // this typeahead still called the old app's path, which only worked
  // because the old app was still running.
  var ENDPOINT = String(ST.__proxy || '/apps/sydneytaps-shipping').replace(/\/+$/, '') + '/locality';
  var DEBOUNCE = 150;
  var MIN_CHARS = 2;
  var MAX_ROWS = 8;
  var DOWN_FOR = 60000;
  var PC = /^\d{4}$/;

  var cache = new Map();
  var downUntil = 0;

  function valid(o) {
    return !!(o && typeof o === 'object' && typeof o.suburb === 'string' && o.suburb.trim()
      && PC.test(String(o.postcode || '').trim()));
  }
  function norm(o) {
    return { suburb: o.suburb.trim(), state: String(o.state || '').trim().toUpperCase(), postcode: String(o.postcode).trim() };
  }
  function get() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return null;
      var o = JSON.parse(raw);
      return valid(o) ? norm(o) : null;
    } catch (e) { return null; }
  }
  function set(o) {
    try {
      if (valid(o)) localStorage.setItem(KEY, JSON.stringify(norm(o)));
      else localStorage.removeItem(KEY);
    } catch (e) { /* private mode: the field still works for the page */ }
    return valid(o) ? norm(o) : null;
  }
  /* "Lane Cove NSW 2064" (rows, inputs) / "Lane Cove 2064" (chip, estimate) */
  function label(o, withState) {
    if (!valid(o)) return '';
    o = norm(o);
    return o.suburb + (withState && o.state ? ' ' + o.state : '') + ' ' + o.postcode;
  }

  function search(q, signal) {
    q = String(q || '').trim().replace(/\s+/g, ' ');
    var key = q.toLowerCase();
    if (q.length < MIN_CHARS) return Promise.resolve([]);
    if (cache.has(key)) return Promise.resolve(cache.get(key));
    if (Date.now() < downUntil) return Promise.resolve([]);
    var url = ENDPOINT + '?q=' + encodeURIComponent(q);
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: signal })
      .then(function (res) {
        if (!res.ok) { downUntil = Date.now() + DOWN_FOR; return []; }
        return res.json().then(function (j) {
          var rows = j && Array.isArray(j.results) ? j.results.filter(valid).map(norm).slice(0, MAX_ROWS) : [];
          cache.set(key, rows);
          return rows;
        }, function () { downUntil = Date.now() + DOWN_FOR; return []; });
      }, function (err) {
        if (err && err.name === 'AbortError') throw err;
        downUntil = Date.now() + DOWN_FOR;
        return [];
      });
  }

  var uid = 0;
  function attach(input, opts) {
    opts = opts || {};
    var list = opts.list;
    if (!input || !list) return null;
    if (!list.id) list.id = 'LocalityList-' + (++uid);
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', list.id);
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('spellcheck', 'false');

    var rows = [];
    var active = -1;
    var timer = null;
    var ctl = null;
    var seq = 0;

    function optId(i) { return list.id + '-opt-' + i; }
    function close() {
      list.hidden = true;
      list.textContent = '';
      rows = []; active = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }
    function setActive(i) {
      // .locality__opt, NOT list.children: the list also carries a
      // .locality__credit row (role=presentation, the GeoNames CC BY credit),
      // and walking every child would let the arrow keys land on it as though
      // it were a suburb — rows[active] would then be undefined on Enter.
      var items = list.querySelectorAll('.locality__opt');
      if (!items.length) return;
      if (i < 0) i = items.length - 1;
      if (i >= items.length) i = 0;
      for (var k = 0; k < items.length; k++) {
        items[k].classList.toggle('is-active', k === i);
        items[k].setAttribute('aria-selected', k === i ? 'true' : 'false');
      }
      active = i;
      input.setAttribute('aria-activedescendant', optId(i));
      var el = items[i];
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }
    function choose(loc) {
      close();
      input.value = label(loc, true);
      input.setCustomValidity('');
      if (opts.onSelect) opts.onSelect(loc);
    }
    function open(found) {
      list.textContent = '';
      rows = found;
      active = -1;
      found.forEach(function (loc, i) {
        var li = document.createElement('li');
        li.className = 'locality__opt';
        li.id = optId(i);
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        var b = document.createElement('b');
        b.textContent = loc.suburb;
        var s = document.createElement('span');
        s.textContent = ' ' + (loc.state ? loc.state + ' ' : '') + loc.postcode;
        li.appendChild(b); li.appendChild(s);
        // mousedown would blur the input (and close the list) before click lands
        li.addEventListener('mousedown', function (e) { e.preventDefault(); });
        li.addEventListener('click', function () { choose(loc); });
        list.appendChild(li);
      });
      // LICENCE OBLIGATION, not decoration. The rows above ARE the GeoNames AU
      // postal-code extract, used under CC BY 4.0, whose readme states the terms
      // plainly: "you can use the dump as long as you give credit to geonames (a
      // link on your website to www.geonames.org is ok)". This link IS that
      // credit, moved here from the footer (Luke, 2026-09-12) so it sits with the
      // data it credits instead of on every page. Do not remove it without
      // removing the data. role="presentation" keeps it out of the option count
      // a screen reader announces. Source of record: supplier-sync docs/localities.md.
      var cr = document.createElement('li');
      cr.className = 'locality__credit';
      cr.setAttribute('role', 'presentation');
      var ca = document.createElement('a');
      ca.href = 'https://www.geonames.org';
      ca.target = '_blank';
      ca.rel = 'noopener nofollow';
      ca.textContent = 'GeoNames';
      cr.appendChild(document.createTextNode('Suburb data by '));
      cr.appendChild(ca);
      cr.appendChild(document.createTextNode(', CC BY 4.0'));
      cr.addEventListener('mousedown', function (e) { e.preventDefault(); });
      list.appendChild(cr);
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      input.removeAttribute('aria-activedescendant');
    }
    function run(v) {
      if (ctl) ctl.abort();
      ctl = typeof AbortController === 'function' ? new AbortController() : null;
      var my = ++seq;
      return search(v, ctl && ctl.signal).then(function (found) {
        if (my !== seq || input.value.trim().replace(/\s+/g, ' ') !== v) return null;
        // a full postcode with ONE match resolves itself — no click needed
        if (PC.test(v) && found.length === 1) { choose(found[0]); return found[0]; }
        if (found.length) open(found); else close();
        return null;
      }, function () { return null; });
    }

    input.addEventListener('input', function () {
      clearTimeout(timer);
      input.setCustomValidity('');
      var v = input.value.trim().replace(/\s+/g, ' ');
      if (v.length < MIN_CHARS) { close(); return; }
      timer = setTimeout(function () { run(v); }, DEBOUNCE);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (list.hidden) { if (rows.length === 0 && input.value.trim().length >= MIN_CHARS) run(input.value.trim()); return; }
        e.preventDefault();
        setActive(e.key === 'ArrowDown' ? active + 1 : active - 1);
      } else if (e.key === 'Enter') {
        if (list.hidden) return;             // the form's submit handler takes it
        e.preventDefault();
        choose(rows[active >= 0 ? active : 0]);
      } else if (e.key === 'Escape') {
        if (list.hidden) return;             // let the popover / page handle it
        e.preventDefault(); e.stopPropagation();
        close();
      } else if (e.key === 'Tab') {
        close();
      }
    });
    input.addEventListener('blur', function () { setTimeout(close, 120); });

    /* resolve(): what the field means right now, for the form's submit —
       {locality} when a suburb is chosen (or the text/postcode has one
       match), {postcode} for a bare 4-digit postcode nothing could name,
       null when the text matches nothing. */
    function resolve() {
      var v = input.value.trim().replace(/\s+/g, ' ');
      clearTimeout(timer);
      if (!list.hidden && rows.length) { var pick = rows[active >= 0 ? active : 0]; choose(pick); return Promise.resolve({ locality: pick }); }
      var isPc = PC.test(v);
      var m = v.match(/^(.*\S)\s+(?:[A-Z]{2,3}\s+)?(\d{4})$/i);   // "Lane Cove NSW 2064" typed or pasted back
      var lookup = isPc ? v : (m ? m[1] : v);
      return search(lookup).then(function (found) {
        if (m) {
          var exact = found.filter(function (r) { return r.postcode === m[2] && r.suburb.toLowerCase() === m[1].toLowerCase(); });
          if (exact.length) { choose(exact[0]); return { locality: exact[0] }; }
        }
        if (isPc) {
          if (found.length === 1) { choose(found[0]); return { locality: found[0] }; }
          if (found.length > 1) { open(found); return null; }
          close();
          return { postcode: v };
        }
        if (found.length === 1 || (found.length > 1 && !m)) { choose(found[0]); return { locality: found[0] }; }
        if (m && PC.test(m[2])) { close(); return { postcode: m[2] }; }
        close();
        return null;
      }, function () { return isPc ? { postcode: v } : null; });
    }

    return { resolve: resolve, close: close, PC: PC };
  }

  ST.locality = { get: get, set: set, label: label, search: search, attach: attach, ENDPOINT: ENDPOINT, PC: PC };
})();
